import {
  VectorStoreStatus,
  GenerateContentParams,
  GenerateContentResponse,
  RoadmapVersion,
  VersionsListResponse,
  LLMMetadata,
} from '../types';
import { api, getErrorMessage, handleServiceError } from './api';
import { CitationReference } from './scientificSearchService';

/**
 * Função auxiliar reutilizável para fazer requisições SSE (Server-Sent Events).
 * Usada por generateContent e improveContent.
 */
type StreamStatus = 'success' | 'initializing' | 'error';

interface StreamSSEOptions {
  llmProvider?: string | null;
  llmModel?: string | null;
  onMetadata?: (metadata: LLMMetadata) => void;
  onContentReplace?: (text: string) => void;
}

async function _streamSSE(
  endpoint: string,
  body: Record<string, unknown>,
  onChunk: (text: string) => void,
  onStatus?: (message: string) => void,
  onReasoning?: (text: string) => void,
  options?: StreamSSEOptions,
  onRefineStart?: () => void,
  onRefineChunk?: (text: string) => void,
): Promise<{ status: StreamStatus; content: string; metadata?: LLMMetadata }> {
  const baseURL = api.defaults.baseURL || '';
  const token = localStorage.getItem('prainte_token');

  // Adiciona provider/model ao body se fornecidos
  const finalBody: Record<string, unknown> = { ...body, stream: true };
  if (options?.llmProvider) {
    finalBody.llm_provider = options.llmProvider;
  }
  if (options?.llmModel) {
    finalBody.llm_model = options.llmModel;
  }

  const response = await fetch(`${baseURL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(finalBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || `HTTP ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Stream não disponível');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';
  let metadataResult: LLMMetadata | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Processar linhas SSE completas
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));

          if (data.type === 'metadata' && options?.onMetadata) {
            const meta: LLMMetadata = {
              provider_used: data.provider_used,
              model_used: data.model_used,
              fallback_reason: data.fallback_reason,
            };
            metadataResult = meta;
            options.onMetadata(meta);
          } else if (data.type === 'reasoning' && onReasoning) {
            onReasoning(data.content);
          } else if (data.type === 'chunk') {
            fullContent += data.content;
            onChunk(data.content);
          } else if (data.type === 'refine_start' && onRefineStart) {
            onRefineStart();
          } else if (data.type === 'refine_chunk' && onRefineChunk) {
            onRefineChunk(data.content);
          } else if (data.type === 'content_replace') {
            fullContent = data.content || '';
            options?.onContentReplace?.(fullContent);
          } else if (data.type === 'refine_fallback') {
            // refinamento falhou — frontend mantém o conteúdo bruto, não faz nada
          } else if (data.type === 'done') {
            return { status: 'success', content: fullContent, metadata: metadataResult };
          } else if (data.type === 'error') {
            throw new Error(data.content);
          } else if (data.type === 'initializing') {
            return { status: 'initializing', content: data.content, metadata: metadataResult };
          } else if (data.type === 'status' && onStatus) {
            onStatus(data.content);
          }
        } catch (e) {
          if (e instanceof Error && !line.includes('"type"')) {
            // Linha não é JSON válido, ignorar
          } else {
            throw e;
          }
        }
      }
    }
  }

  // Se chegou aqui sem 'done', retornar o que acumulou
  return { status: (fullContent ? 'success' : 'error') as StreamStatus, content: fullContent, metadata: metadataResult };
}


export const elaborationService = {
  // Vector Store Status
  getVectorStoreStatus: async (proposalId: number): Promise<VectorStoreStatus> => {
    try {
      const response = await api.get(`/proposals/${proposalId}/vector-store-status`);
      return response?.data;
    } catch (error: any) {
      // 500 com "não encontrado" = vector store ainda não criado — estado esperado, não logar no Sentry
      const detail: string = error?.response?.data?.detail ?? '';
      if (detail.includes('não encontrado') || detail.includes('nao encontrado')) {
        return {
          vector_store_id: undefined,
          vector_store_status: 'unknown',
          file_counts: { total: 0, completed: 0, in_progress: 0, failed: 0 },
          files: [],
        };
      }
      handleServiceError(error, { feature: 'elaboration', action: 'get_vector_store_status' });
      throw error;
    }
  },

  // Gerar conteúdo com IA (streaming por default)
  generateContent: async (
    proposalId: number,
    params: GenerateContentParams,
    onChunk?: (text: string) => void,
    onReasoning?: (text: string) => void,
    llmMeta?: { provider?: string | null; model?: string | null },
    onMetadata?: (metadata: LLMMetadata) => void,
    onRefineStart?: () => void,
    onRefineChunk?: (text: string) => void,
    onContentReplace?: (text: string) => void,
  ): Promise<GenerateContentResponse> => {
    try {
      console.log('[ElaborationService] Gerando conteúdo (streaming):', {
        topic: params.topic,
        reasoningLevel: params.reasoningLevel,
        useGeneralContext: params.useGeneralContext,
        useWebSearch: params.useWebSearch,
        llmProvider: llmMeta?.provider,
      });

      const result = await _streamSSE(
        '/roadmap/generate-content',
        {
          proposal_id: proposalId,
          topic_name: params.topic,
          topic_instruction: params.instruction,
          user_instructions: params.userInstructions || '',
          use_general_context: params.useGeneralContext,
          use_web_search: params.useWebSearch,
          reasoning_level: params.reasoningLevel || 'high',
        },
        onChunk || (() => {}),
        undefined,
        onReasoning,
        {
          llmProvider: llmMeta?.provider,
          llmModel: llmMeta?.model,
          onMetadata,
          onContentReplace,
        },
        onRefineStart,
        onRefineChunk,
      );

      console.log(`[ElaborationService] Streaming concluído: status=${result.status}, ${result.content.length} chars`);

      if (result.status === 'initializing') {
        return {
          status: 'initializing',
          message: result.content,
          content: '',
        };
      }

      return {
        status: result.status,
        content: result.content,
        message: result.status === 'success' ? 'Conteúdo gerado com sucesso' : '',
      };

    } catch (error: any) {
      console.error('[ElaborationService] Erro ao gerar conteúdo:', error);
      throw new Error(error.message || 'Erro ao gerar conteúdo');
    }
  },

  // Melhorar conteúdo existente (streaming por default)
  improveContent: async (
    proposalId: number,
    params: GenerateContentParams & { currentContent: string; citationReferences?: CitationReference[] },
    onChunk?: (text: string) => void,
    onReasoning?: (text: string) => void,
    llmMeta?: { provider?: string | null; model?: string | null },
    onMetadata?: (metadata: LLMMetadata) => void,
    onContentReplace?: (text: string) => void,
  ): Promise<GenerateContentResponse> => {
    try {
      console.log('[ElaborationService] Melhorando conteúdo (streaming):', {
        topic: params.topic,
        reasoningLevel: params.reasoningLevel,
        contentLength: params.currentContent.length,
        llmProvider: llmMeta?.provider,
        citationReferencesCount: params.citationReferences?.length || 0,
      });

      const result = await _streamSSE(
        '/roadmap/improve-content',
        {
          proposal_id: proposalId,
          topic_name: params.topic,
          topic_instruction: params.instruction,
          existing_content: params.currentContent,
          user_instructions: params.userInstructions || '',
          use_general_context: params.useGeneralContext,
          use_web_search: params.useWebSearch,
          document_ids: params.documentIds && params.documentIds.length > 0 ? params.documentIds : null,
          reasoning_level: params.reasoningLevel || 'default',
          citation_references: params.citationReferences && params.citationReferences.length > 0
            ? params.citationReferences.map(ref => ref.source === 'manual'
                ? { source: 'manual', title: ref.title, document_id: ref.documentId ?? null, text_content: ref.textContent ?? null }
                : { source: ref.source, title: ref.title, authors: ref.authors, year: ref.year, venue: ref.venue })
            : null,
        },
        onChunk || (() => {}),
        undefined,
        onReasoning,
        {
          llmProvider: llmMeta?.provider,
          llmModel: llmMeta?.model,
          onMetadata,
          onContentReplace,
        },
      );

      console.log(`[ElaborationService] Streaming melhoria concluído: status=${result.status}, ${result.content.length} chars`);

      if (result.status === 'initializing') {
        return {
          status: 'initializing',
          message: result.content,
          content: '',
        };
      }

      return {
        status: result.status,
        content: result.content,
        message: result.status === 'success' ? 'Conteúdo melhorado com sucesso' : '',
      };

    } catch (error: any) {
      console.error('[ElaborationService] Erro ao melhorar conteúdo:', error);
      throw new Error(error.message || 'Erro ao melhorar conteúdo');
    }
  },

  // Exportar proposta
  exportProposal: async (proposalId: number, format: 'pdf' | 'docx', excludeIds: number[] = [], language?: string, layout?: string): Promise<{ blob: Blob; sectionsWithoutTranslation: string[] }> => {
    try {
      const queryParams = new URLSearchParams();
      if (excludeIds.length > 0) queryParams.set('exclude_ids', excludeIds.join(','));
      if (language) queryParams.set('language', language);
      if (layout) queryParams.set('layout', layout);
      const queryString = queryParams.toString();
      const params = queryString ? `?${queryString}` : '';
      const endpoint = format === 'pdf'
        ? `/proposals/${proposalId}/export-pdf${params}`
        : `/proposals/${proposalId}/export-docx${params}`;

      console.log('[ElaborationService] Exportando proposta:', { proposalId, format, endpoint, language, layout });

      const response = await api.get(endpoint, {
        responseType: 'blob',
      });

      const sectionsHeader = response?.headers?.['x-sections-without-translation'] || '';
      const sectionsWithoutTranslation = sectionsHeader
        ? sectionsHeader.split(',').filter(Boolean).map((s: string) => decodeURIComponent(s))
        : [];

      // Validar se a resposta é um blob válido
      const blob = response?.data;
      if (!blob || !(blob instanceof Blob)) {
        throw new Error('Resposta inválida do servidor: não é um arquivo válido');
      }

      // Verificar o tipo MIME do blob
      const expectedMimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      
      // Se o blob for muito pequeno, pode ser um erro JSON
      if (blob.size < 100) {
        console.warn('[ElaborationService] Blob muito pequeno, possível erro:', blob.size);
        const text = await blob.text();
        console.warn('[ElaborationService] Conteúdo do blob:', text);
        
        // Tentar parsear como JSON para verificar se é mensagem de erro
        try {
          const errorData = JSON.parse(text);
          if (errorData.detail || errorData.message) {
            throw new Error(errorData.detail || errorData.message);
          }
        } catch (e) {
          // Não é JSON, continuar
        }
      }

      // Verificar se o tipo MIME está correto (quando disponível)
      if (blob.type && blob.type !== '' && blob.type !== expectedMimeType) {
        console.warn(`[ElaborationService] Tipo MIME inesperado: esperado ${expectedMimeType}, recebido ${blob.type}`);
      }

      console.log('[ElaborationService] Exportação concluída:', { 
        size: blob.size, 
        type: blob.type,
        format 
      });

      return { blob, sectionsWithoutTranslation };
    } catch (error) {
      console.error('[ElaborationService] Erro ao exportar:', error);

      // Melhorar mensagem de erro baseado no tipo de erro
      if (error instanceof Error) {
        if (error.message.includes('não é um arquivo válido')) {
          throw new Error('Erro ao exportar: O servidor retornou uma resposta inválida. Tente novamente.');
        }
        if (error.message.includes('Network Error')) {
          throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
        }
        if (error.message.includes('timeout')) {
          throw new Error('Tempo limite excedido. A exportação pode estar demorando muito. Tente novamente.');
        }
      }
      
      handleServiceError(error, { feature: 'elaboration', action: 'export_proposal' });
      throw error; // Re-throw para manter a tipagem correta
    }
  },

  /**
   * Traduz o content já existente de uma seção para um idioma-alvo. Não sobrescreve o
   * content original — a tradução fica salva à parte (RoadMap.translations) e é
   * retornada aqui para preview.
   */
  translateContent: async (
    proposalId: number,
    params: { topic: string; existingContent: string; targetLanguage: string; reasoningLevel?: string },
  ): Promise<string> => {
    try {
      const response = await api.post('/roadmap/translate-content', {
        proposal_id: proposalId,
        topic_name: params.topic,
        existing_content: params.existingContent,
        target_language: params.targetLanguage,
        reasoning_level: params.reasoningLevel || 'default',
      });
      return response?.data?.translated_content || '';
    } catch (error) {
      return handleServiceError(error, { feature: 'elaboration', action: 'translate_content' });
    }
  },

  /**
   * Traduz todas as seções do roteiro que já têm conteúdo, em lote.
   */
  translateAllSections: async (
    proposalId: number,
    targetLanguage: string,
    reasoningLevel?: string,
  ): Promise<{ translated_count: number; skipped_count: number; error_count: number }> => {
    try {
      const response = await api.post('/roadmap/translate-all', {
        proposal_id: proposalId,
        target_language: targetLanguage,
        reasoning_level: reasoningLevel || 'default',
      });
      return response?.data;
    } catch (error) {
      return handleServiceError(error, { feature: 'elaboration', action: 'translate_all' });
    }
  },
};

export const versionService = {
  // Listar versões de um roadmap item
  list: async (roadmapId: number, limit: number = 5): Promise<VersionsListResponse> => {
    try {
      const response = await api.get(`/roadmap-history/${roadmapId}/versions`, {
        params: { limit },
      });

      return {
        versions: response?.data?.versions || [],
        total_versions: response?.data?.total_versions || 0,
      };
    } catch (error) {
      handleServiceError(error, { feature: 'elaboration', action: 'list' });
      throw error; // Re-throw para manter a tipagem correta
    }
  },

  // Salvar versão manual
  saveManual: async (roadmapId: number, content: string): Promise<RoadmapVersion> => {
    try {
      const response = await api.post(`/roadmap-history/${roadmapId}/save-manual`, {
        content,
      });

      return {
        id: 0,
        roadmap_id: response?.data?.roadmap_id,
        version_number: response?.data?.version_number || 0,
        content: content,
        created_by_operation: 'manual_save',
        created_at: new Date().toISOString(),
        metadata: {
          manual_edit: true,
          content_length: response?.data?.content_length,
        },
      };
    } catch (error) {
      handleServiceError(error, { feature: 'elaboration', action: 'save_manual' });
      throw error; // Re-throw para manter a tipagem correta
    }
  },

  // Reverter para versão específica
  revert: async (roadmapId: number, versionNumber: number): Promise<{ success: boolean; new_version_number: number }> => {
    try {
      const response = await api.post(`/roadmap-history/${roadmapId}/revert/${versionNumber}`);

      return {
        success: true,
        new_version_number: response?.data?.reverted_to_version || versionNumber,
      };
    } catch (error) {
      handleServiceError(error, { feature: 'elaboration', action: 'revert' });
      throw error; // Re-throw para manter a tipagem correta
    }
  },
};
